/**
 * pds.tsx — Premium Design System component vocabulary.
 *
 * One-file library so every page can `import { Bento, GlassCard, ... } from
 * '../../design-system/pds'`. Keeps the import surface tiny while still
 * offering the full set of premium-dashboards primitives.
 *
 * Conventions:
 *   - Everything respects the global "Pearl + Coral" palette.
 *   - Cards are translucent over a tinted canvas (the canvas leaks through).
 *   - One accent color across the system (#FF7A5C — coral). The PAID/lime
 *     accent is reserved for `status="paid"` only.
 *   - Corners ≥ rounded-3xl; never rounded-md/lg.
 *   - No visible borders on cards; depth via shadow + space + blur only.
 */

import * as React from 'react';
import { motion } from 'framer-motion';
import { Link, type LinkProps } from 'react-router-dom';
import { ArrowUpRight, ChevronDown, Search, X, Check, Plus, Mic } from 'lucide-react';
import { cn } from '../lib/utils';

/* ────────────────────────────────────────────────────────────────────
 * App shell containers
 * ──────────────────────────────────────────────────────────────────── */

export function PageShell({
  children,
  max = 'max-w-[1480px]',
  className,
}: {
  children: React.ReactNode;
  max?: string;
  className?: string;
}) {
  return (
    <div className={cn('px-6 sm:px-8 lg:px-10 py-8 lg:py-10 mx-auto w-full', max, className)}>
      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Cards
 * ──────────────────────────────────────────────────────────────────── */

type CardProps = {
  children: React.ReactNode;
  className?: string;
  padding?: string;     // tailwind padding class (default p-7)
  as?: React.ElementType;
  motionDelay?: number; // seconds
  hover?: boolean;
};

export function GlassCard({
  children,
  className,
  padding = 'p-7',
  as: As = 'div',
  motionDelay = 0,
  hover = false,
}: CardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 22, delay: motionDelay }}
      whileHover={hover ? { y: -3 } : undefined}
      className={cn(
        'rounded-3xl bg-white/[0.85] dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10',
        padding,
        className,
      )}
    >
      <As className="contents">{children}</As>
    </motion.div>
  );
}

export function DarkCard({
  children,
  className,
  padding = 'p-7',
  motionDelay = 0,
  hover = false,
}: CardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 22, delay: motionDelay }}
      whileHover={hover ? { y: -3 } : undefined}
      className={cn(
        'rounded-3xl bg-ink-900 text-white shadow-card-lg ring-1 ring-white/10',
        padding,
        className,
      )}
    >
      {children}
    </motion.div>
  );
}

export function CoralCard({
  children,
  className,
  padding = 'p-7',
  motionDelay = 0,
}: CardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 22, delay: motionDelay }}
      className={cn(
        'rounded-3xl bg-coral-gradient text-white shadow-cta ring-1 ring-white/10 overflow-hidden',
        padding,
        className,
      )}
    >
      {children}
    </motion.div>
  );
}

export function LavenderCard({
  children,
  className,
  padding = 'p-7',
  motionDelay = 0,
}: CardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 22, delay: motionDelay }}
      className={cn(
        'rounded-3xl bg-lavender-gradient text-ink dark:text-white shadow-card-lg ring-1 ring-white/30 overflow-hidden',
        padding,
        className,
      )}
    >
      {children}
    </motion.div>
  );
}

/** Dotted-texture promo card — used for "upgrade" CTAs. */
export function DottedPromoCard({
  title,
  body,
  ctaLabel,
  to,
  onClick,
  className,
}: {
  title: string;
  body: string;
  ctaLabel: string;
  to?: string;
  onClick?: () => void;
  className?: string;
}) {
  const Cta: React.ReactElement = to ? (
    <Link
      to={to}
      className="mt-5 inline-flex w-full items-center justify-between rounded-full bg-white dark:bg-white/10 px-5 py-2.5 shadow-pill dark:shadow-none text-[14px] font-semibold text-ink dark:text-white hover:bg-coral dark:hover:bg-coral hover:text-white transition-colors"
    >
      <span>{ctaLabel}</span>
      <ArrowUpRight className="h-4 w-4" />
    </Link>
  ) : (
    <button
      type="button"
      onClick={onClick}
      className="mt-5 inline-flex w-full items-center justify-between rounded-full bg-white dark:bg-white/10 px-5 py-2.5 shadow-pill dark:shadow-none text-[14px] font-semibold text-ink dark:text-white hover:bg-coral dark:hover:bg-coral hover:text-white transition-colors"
    >
      <span>{ctaLabel}</span>
      <ArrowUpRight className="h-4 w-4" />
    </button>
  );

  return (
    <div
      className={cn(
        'rounded-3xl bg-pearl-100 dark:bg-white/[0.04] pds-dotted-coral shadow-card p-7 relative overflow-hidden',
        className,
      )}
    >
      <h3 className="text-title-lg text-ink dark:text-white mb-2">{title}</h3>
      <p className="text-[13.5px] text-ink/65 dark:text-white/60 max-w-xs leading-relaxed">{body}</p>
      {Cta}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Bento grid
 * ──────────────────────────────────────────────────────────────────── */

export function BentoGrid({
  children,
  cols = 12,
  gap = 'gap-4 lg:gap-5',
  className,
}: {
  children: React.ReactNode;
  cols?: number;
  gap?: string;
  className?: string;
}) {
  return (
    <div
      className={cn('grid auto-rows-min', gap, className)}
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Pills, chips, tags
 * ──────────────────────────────────────────────────────────────────── */

type PillSize = 'sm' | 'md' | 'lg';
type PillVariant = 'light' | 'solid' | 'dark' | 'coral' | 'lime' | 'lavender' | 'ghost' | 'outline';

export function Pill({
  children,
  variant = 'light',
  size = 'md',
  className,
  onClick,
  as,
}: {
  children: React.ReactNode;
  variant?: PillVariant;
  size?: PillSize;
  className?: string;
  onClick?: () => void;
  as?: 'button' | 'span' | 'div';
}) {
  const Tag = (as ?? (onClick ? 'button' : 'span')) as React.ElementType;
  const variants: Record<PillVariant, string> = {
    light:    'bg-white/72 dark:bg-white/10 backdrop-blur-md text-ink dark:text-white shadow-pill dark:shadow-none',
    solid:    'bg-white dark:bg-white/10 text-ink dark:text-white shadow-pill dark:shadow-none',
    dark:     'bg-ink text-white',
    coral:    'bg-coral text-white shadow-pill dark:shadow-none',
    lime:     'bg-lime-neon text-ink dark:shadow-none',
    lavender: 'bg-lavender-100 dark:bg-lavender-400/20 text-lavender-700 dark:text-lavender-200',
    ghost:    'bg-white/30 dark:bg-white/[0.06] backdrop-blur-md text-ink dark:text-white',
    outline:  'border border-ink/15 dark:border-white/15 text-ink dark:text-white',
  };
  const sizes: Record<PillSize, string> = {
    sm: 'px-3 py-1 text-[11px]',
    md: 'px-4 py-1.5 text-[12.5px]',
    lg: 'px-5 py-2.5 text-[13.5px]',
  };
  return (
    <Tag
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors',
        variants[variant],
        sizes[size],
        className,
      )}
    >
      {children}
    </Tag>
  );
}

const STATUS_STYLES = {
  paid:     'bg-lime-neon text-ink',
  overdue:  'bg-rose-500/15 text-rose-700 ring-1 ring-rose-500/30 dark:bg-rose-500/25 dark:text-rose-200 dark:ring-rose-500/40',
  sent:     'bg-coral/12 text-coral-700 ring-1 ring-coral/30 dark:bg-coral/20 dark:text-coral-200 dark:ring-coral/40',
  draft:    'bg-ink/5 text-ink/65 ring-1 ring-ink/10 dark:bg-white/10 dark:text-white/70 dark:ring-white/15',
  scheduled:'bg-lavender-100 text-lavender-700 ring-1 ring-lavender-200 dark:bg-lavender-400/20 dark:text-lavender-200 dark:ring-lavender-400/30',
  active:   'bg-lime-neon text-ink',
  inactive: 'bg-ink/5 text-ink/55 ring-1 ring-ink/10 dark:bg-white/10 dark:text-white/55 dark:ring-white/15',
  pending:  'bg-coral/12 text-coral-700 ring-1 ring-coral/30 dark:bg-coral/20 dark:text-coral-200 dark:ring-coral/40',
  void:     'bg-ink/5 text-ink/45 ring-1 ring-ink/10 dark:bg-white/10 dark:text-white/45 dark:ring-white/15',
  partial:  'bg-coral/15 text-coral-700 ring-1 ring-coral/25 dark:bg-coral/20 dark:text-coral-200 dark:ring-coral/40',
  failed:   'bg-rose-500/15 text-rose-700 ring-1 ring-rose-500/30 dark:bg-rose-500/25 dark:text-rose-200 dark:ring-rose-500/40',
  approved: 'bg-lime-neon text-ink',
  rejected: 'bg-rose-500/15 text-rose-700 ring-1 ring-rose-500/30 dark:bg-rose-500/25 dark:text-rose-200 dark:ring-rose-500/40',
} as const;

export type StatusKind = keyof typeof STATUS_STYLES;

export function StatusChip({
  status,
  children,
  className,
}: {
  status: StatusKind | string;
  children?: React.ReactNode;
  className?: string;
}) {
  const key = (status in STATUS_STYLES ? status : 'draft') as StatusKind;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.10em]',
        STATUS_STYLES[key],
        className,
      )}
    >
      {children ?? status}
    </span>
  );
}

export function FilterChip({
  label,
  onDismiss,
  className,
}: {
  label: string;
  onDismiss?: () => void;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex items-center gap-2 px-3.5 py-1.5 bg-white dark:bg-white/10 text-ink dark:text-white rounded-full shadow-pill dark:shadow-none text-[12.5px] font-medium', className)}>
      <span>{label}</span>
      {onDismiss && (
        <button onClick={onDismiss} className="text-ink/40 dark:text-white/40 hover:text-ink dark:hover:text-white" aria-label="Dismiss">
          <X size={12} strokeWidth={2.5} />
        </button>
      )}
    </span>
  );
}

export function Eyebrow({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  /** Native tooltip — worth passing wherever the eyebrow may `truncate`, so a
   *  clipped label is still readable on hover. */
  title?: string;
}) {
  return (
    <p
      title={title}
      className={cn('text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55', className)}
    >
      {children}
    </p>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Buttons & action elements
 * ──────────────────────────────────────────────────────────────────── */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'dark' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

const BTN_VARIANTS: Record<ButtonVariant, string> = {
  primary:   'bg-coral text-white shadow-cta hover:bg-coral-600',
  secondary: 'bg-white dark:bg-white/10 text-ink dark:text-white shadow-pill dark:shadow-none hover:shadow-pill-active dark:hover:bg-white/15',
  ghost:     'bg-white/40 dark:bg-white/[0.06] backdrop-blur-md text-ink dark:text-white hover:bg-white/70 dark:hover:bg-white/15',
  dark:      'bg-ink text-white shadow-cta-dark hover:bg-ink-800',
  outline:   'border border-ink/15 dark:border-white/15 text-ink dark:text-white hover:bg-ink/5 dark:hover:bg-white/10',
};
const BTN_SIZES: Record<ButtonSize, string> = {
  sm: 'h-9  px-4 text-[12.5px] gap-1.5',
  md: 'h-11 px-5 text-[13.5px] gap-2',
  lg: 'h-12 px-7 text-[14.5px] gap-2.5',
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  className,
  fullWidth,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
}) {
  return (
    <button
      {...rest}
      className={cn(
        'inline-flex items-center justify-center rounded-full font-semibold transition-all duration-200 focus-coral disabled:opacity-50',
        BTN_VARIANTS[variant],
        BTN_SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function ButtonLink({
  children,
  variant = 'primary',
  size = 'md',
  className,
  fullWidth,
  ...rest
}: LinkProps & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
}) {
  return (
    <Link
      {...rest}
      className={cn(
        'inline-flex items-center justify-center rounded-full font-semibold transition-all duration-200 focus-coral',
        BTN_VARIANTS[variant],
        BTN_SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {children}
    </Link>
  );
}

export function IconButton({
  icon,
  variant = 'ghost',
  size = 'md',
  onClick,
  ariaLabel,
  className,
}: {
  icon: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  onClick?: () => void;
  ariaLabel?: string;
  className?: string;
}) {
  const sizeCls = size === 'sm' ? 'h-8 w-8' : size === 'lg' ? 'h-12 w-12' : 'h-10 w-10';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center justify-center rounded-full transition-all focus-coral',
        BTN_VARIANTS[variant],
        sizeCls,
        className,
      )}
    >
      {icon}
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Form elements
 * ──────────────────────────────────────────────────────────────────── */

export function SearchPill({
  placeholder = 'Search...',
  value,
  onChange,
  className,
}: {
  placeholder?: string;
  value?: string;
  onChange?: (v: string) => void;
  className?: string;
}) {
  return (
    <label className={cn('inline-flex items-center gap-2 h-11 px-4 rounded-full bg-white/70 dark:bg-white/[0.06] backdrop-blur-md text-ink dark:text-white shadow-pill dark:shadow-none text-[13px] focus-within:ring-2 focus-within:ring-coral/30', className)}>
      <Search className="h-3.5 w-3.5 text-ink/45 dark:text-white/45" strokeWidth={2} />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        className="bg-transparent border-0 outline-none w-full placeholder:text-ink/40 dark:placeholder:text-white/40"
      />
    </label>
  );
}

export function DropdownPill({
  label,
  onClick,
  className,
  active,
}: {
  label: React.ReactNode;
  onClick?: () => void;
  className?: string;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 h-10 px-4 rounded-full shadow-pill dark:shadow-none text-[13px] font-semibold transition-colors',
        active ? 'bg-ink dark:bg-white dark:text-ink text-white' : 'bg-white dark:bg-white/10 text-ink dark:text-white hover:bg-white/90 dark:hover:bg-white/15',
        className,
      )}
    >
      <span>{label}</span>
      <ChevronDown size={14} strokeWidth={2} className={cn(active ? 'text-white/70 dark:text-ink/60' : 'text-ink/50 dark:text-white/50')} />
    </button>
  );
}

export function DatePill({
  day,
  weekday,
  month,
  className,
}: {
  day: number | string;
  weekday: string;
  month: string;
  className?: string;
}) {
  return (
    <div className={cn('inline-flex items-center gap-3 bg-white dark:bg-white/10 rounded-full p-1 pr-5 shadow-pill dark:shadow-none', className)}>
      <div className="w-11 h-11 rounded-full bg-pearl-200 dark:bg-white/15 flex items-center justify-center font-bold text-[18px] text-ink dark:text-white">
        {day}
      </div>
      <div className="text-[12.5px] leading-tight text-ink dark:text-white">
        <div className="font-semibold">{weekday},</div>
        <div className="text-ink/65 dark:text-white/65">{month}</div>
      </div>
    </div>
  );
}

export function Input({
  className,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...rest}
      className={cn(
        'w-full h-11 px-4 rounded-2xl bg-white/80 dark:bg-white/[0.05] backdrop-blur-md text-[14px] text-ink dark:text-white placeholder:text-ink/40 dark:placeholder:text-white/40 shadow-pill ring-1 ring-ink/8 dark:ring-white/10 focus:outline-none focus:ring-2 focus:ring-coral/40 transition-all',
        className,
      )}
    />
  );
}

export function Textarea({
  className,
  ...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...rest}
      className={cn(
        'w-full px-4 py-3 rounded-2xl bg-white/80 dark:bg-white/[0.05] backdrop-blur-md text-[14px] text-ink dark:text-white placeholder:text-ink/40 dark:placeholder:text-white/40 shadow-pill ring-1 ring-ink/8 dark:ring-white/10 focus:outline-none focus:ring-2 focus:ring-coral/40 transition-all',
        className,
      )}
    />
  );
}

export function FieldLabel({
  children,
  htmlFor,
  className,
}: {
  children: React.ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={cn('text-[12px] font-semibold text-ink/70 dark:text-white/70 mb-1.5 block', className)}>
      {children}
    </label>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Stat displays
 * ──────────────────────────────────────────────────────────────────── */

export function BigStat({
  icon,
  value,
  label,
  className,
}: {
  icon?: React.ReactNode;
  value: React.ReactNode;
  label: string;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      {icon && (
        <div className="h-10 w-10 rounded-full bg-white/60 dark:bg-white/10 backdrop-blur-md flex items-center justify-center text-ink dark:text-white shadow-pill dark:shadow-none">
          {icon}
        </div>
      )}
      <div>
        <div className="text-stat-lg leading-none tabular-nums text-ink dark:text-white">{value}</div>
        <div className="text-[12.5px] text-ink/60 dark:text-white/60 mt-1">{label}</div>
      </div>
    </div>
  );
}

export function HeroStat({
  value,
  currency,
  label,
  subLabel,
  className,
}: {
  value: React.ReactNode;
  currency?: string;
  label: string;
  subLabel?: string;
  className?: string;
}) {
  return (
    <div className={cn('text-ink dark:text-white', className)}>
      <div className="flex items-baseline gap-2">
        <span className="text-stat-lg tabular-nums">{value}</span>
        {currency && <span className="text-[22px] text-ink/60 dark:text-white/60 font-medium">{currency}</span>}
      </div>
      <div className="text-[13px] text-ink/60 dark:text-white/60 mt-1.5">
        <span>{label}</span>
        {subLabel && <span> • <span className="font-semibold text-ink dark:text-white">{subLabel}</span></span>}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Tabs
 * ──────────────────────────────────────────────────────────────────── */

export function TabStrip<T extends string>({
  tabs,
  active,
  onChange,
  variant = 'light',
  className,
}: {
  tabs: ReadonlyArray<T> | Array<{ id: T; label: React.ReactNode }>;
  active: T;
  onChange: (t: T) => void;
  variant?: 'light' | 'dark';
  className?: string;
}) {
  const items = (tabs as ReadonlyArray<unknown>).map((t) =>
    typeof t === 'string' ? { id: t as T, label: t } : (t as { id: T; label: React.ReactNode }),
  );

  return (
    <div
      className={cn(
        'inline-flex items-center p-1 rounded-full shadow-pill dark:shadow-none',
        variant === 'dark' ? 'bg-ink/80 backdrop-blur-md' : 'bg-white/55 dark:bg-white/[0.06] backdrop-blur-md',
        className,
      )}
    >
      {items.map(({ id, label }) => {
        const isActive = id === active;
        return (
          <button
            key={String(id)}
            onClick={() => onChange(id)}
            className={cn(
              'px-4 py-2 rounded-full text-[12.5px] font-semibold transition-colors whitespace-nowrap',
              isActive
                ? variant === 'dark'
                  ? 'bg-white text-ink shadow-pill-active'
                  : 'bg-white dark:bg-white/15 text-ink dark:text-white shadow-pill-active dark:shadow-none'
                : variant === 'dark'
                  ? 'text-white/65 hover:text-white'
                  : 'text-ink/60 dark:text-white/60 hover:text-ink dark:hover:text-white',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * List rows
 * ──────────────────────────────────────────────────────────────────── */

export function Row({
  children,
  className,
  onClick,
  to,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  to?: string;
}) {
  const cls = cn(
    'flex items-center gap-3 py-3 px-4 rounded-2xl transition-colors text-ink dark:text-white hover:bg-white/60 dark:hover:bg-white/[0.05]',
    className,
  );
  if (to) return <Link to={to} className={cls}>{children}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={cls + ' text-left w-full'}>{children}</button>;
  return <div className={cls}>{children}</div>;
}

export function Avatar({
  name,
  src,
  size = 'md',
  className,
}: {
  name?: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}) {
  const sizes = {
    sm: 'h-7 w-7 text-[10px]',
    md: 'h-9 w-9 text-[12px]',
    lg: 'h-11 w-11 text-[14px]',
    xl: 'h-14 w-14 text-[16px]',
  } as const;
  const initial = (name || '?').charAt(0).toUpperCase();
  return src ? (
    <img
      src={src}
      alt={name ?? ''}
      className={cn('rounded-full object-cover ring-1 ring-ink/8 dark:ring-white/15', sizes[size], className)}
    />
  ) : (
    <div
      className={cn(
        'rounded-full bg-ink text-white flex items-center justify-center font-bold ring-1 ring-ink/10',
        sizes[size],
        className,
      )}
    >
      {initial}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Misc helpers
 * ──────────────────────────────────────────────────────────────────── */

export function EmptyHint({
  to,
  label,
  cta,
  className,
}: {
  to: string;
  label: string;
  cta: string;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'block py-4 px-4 rounded-2xl border border-dashed border-ink/12 dark:border-white/15 hover:border-ink/25 dark:hover:border-white/30 transition-colors',
        className,
      )}
    >
      <p className="text-[13px] text-ink/55 dark:text-white/55">{label}</p>
      <p className="text-[13.5px] font-semibold text-ink dark:text-white inline-flex items-center gap-1 mt-1">
        {cta} <ArrowUpRight className="h-3.5 w-3.5" />
      </p>
    </Link>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  action,
  className,
}: {
  eyebrow?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6', className)}>
      <div>
        {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
        <h2 className="font-light text-display-page text-ink dark:text-white leading-tight">{title}</h2>
        {description && (
          <p className="text-[14px] text-ink/60 dark:text-white/60 mt-2 max-w-xl">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <div className={cn('h-px bg-ink/8 dark:bg-white/10 my-6', className)} />;
}

export function Icons() { return null; } // keep file as module

/* Re-exports for convenience */
export { ArrowUpRight, ChevronDown, Search, X, Check, Plus, Mic };
